import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Truck, 
  Package, 
  Zap, 
  Layers, 
  Shield, 
  ArrowUpDown,
  ArrowRight,
  Clock,
  CheckCircle,
  Star,
  Globe,
  Users,
  Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useSiteContent, useStaticTranslation } from '../context/SiteContentContext';
import { useInitScrollAnimations } from '../hooks/useScrollAnimation';
import { usePageTitle } from '../hooks/usePageTitle';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tetralog truck images - local paths
const TRUCK_IMAGES = {
  hero: '/images/truck-hero.jpg',
  snow: '/images/truck-snow.jpg',
  port: '/images/truck-port.jpg'
};

const iconMap = {
  Truck,
  Package,
  Zap,
  Layers,
  Shield,
  ArrowUpDown,
};

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [homeContent, setHomeContent] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { t, language } = useSiteContent();
  const { st } = useStaticTranslation();
  
  // Set page title
  usePageTitle('home');
  
  // Initialize scroll animations
  useInitScrollAnimations();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, homeRes, testimonialsRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/home-content`),
          axios.get(`${API}/testimonials`)
        ]);
        setServices(servicesRes.data.services);
        setHomeContent(homeRes.data);
        setTestimonials(testimonialsRes.data.testimonials || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Dynamic stats from database or defaults
  const stats = homeContent?.stats?.map((stat, index) => ({
    number: stat.number,
    label: language === 'bg' ? stat.label_bg : stat.label_en,
    icon: [Clock, Globe, Users, Award][index] || Clock
  })) || [
    { number: '20+', label: language === 'bg' ? 'Години опит' : 'Years Experience', icon: Clock },
    { number: '28+', label: language === 'bg' ? 'Обслужени държави' : 'Countries Served', icon: Globe },
    { number: '900+', label: language === 'bg' ? 'Доволни клиенти' : 'Happy Clients', icon: Users },
    { number: '100%', label: language === 'bg' ? 'Лицензирани и застраховани' : 'Licensed & Insured', icon: Award },
  ];

  // Hero image from database or default
  const heroImage = homeContent?.hero_image || '/images/truck-hero.jpg';

  // Why Choose Us features
  const whyChooseFeatures = language === 'bg' 
    ? (homeContent?.why_choose_features_bg || [
        'Лицензиран EU превозвач с пълна застраховка',
        'GPS проследяване в реално време',
        'EURO 6 флот с достъп до ECO зони',
        'Професионални услуги за опаковане',
        'Екип за клиентска поддръжка'
      ])
    : (homeContent?.why_choose_features_en || [
        'Licensed EU carrier with full insurance coverage',
        'Real-time GPS tracking on all shipments',
        'EURO 6 fleet with ECO zone access',
        'Professional packing and handling services',
        'Dedicated customer support team'
      ]);

  // Hero content from database
  const heroTitle = (language === 'bg' ? homeContent?.hero_title_bg : homeContent?.hero_title_en) || 
    (language === 'bg' ? 'Бърз, надежден транспорт в Европа и извън нея' : 'Fast, Reliable Transport Across Europe and Beyond');
  const heroSubtitle = (language === 'bg' ? homeContent?.hero_subtitle_bg : homeContent?.hero_subtitle_en) ||
    (language === 'bg' ? 'МЕЖДУНАРОДЕН ТРАНСПОРТ И ЛОГИСТИКА' : 'INTERNATIONAL TRANSPORT & LOGISTICS');

  // Why Choose Us title from database - split into parts for styling
  const whyChooseTitleFull = (language === 'bg' ? homeContent?.why_choose_title_bg : homeContent?.why_choose_title_en) ||
    (language === 'bg' ? 'Доверени от хиляди доволни клиенти' : 'Trusted by Thousands of Satisfied Clients');
  
  // Split title to highlight last 2 words in red
  const titleWords = whyChooseTitleFull.split(' ');
  const whyChooseTitleMain = titleWords.slice(0, -2).join(' ');
  const whyChooseTitleHighlight = titleWords.slice(-2).join(' ');
  
  const whyChooseSubtitle = (language === 'bg' ? homeContent?.why_choose_subtitle_bg : homeContent?.why_choose_subtitle_en) ||
    (language === 'bg' ? 'Съчетаваме десетилетия опит с модерни технологии за несравними транспортни услуги в Европа.' : 'We combine decades of experience with modern technology to deliver unparalleled transport services across Europe.');

  // Show loader while data is loading
  if (isLoading) {
    return (
      <div className="page-loader">
        <div className="page-loader-spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="home-page" className={`bg-slate-50 overflow-x-hidden ${isLoading ? 'page-loading' : 'page-loaded'}`}>
      <SEO page="home" />
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center"
        style={{
          backgroundImage: `url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-white/40" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-32 md:py-40">
          <div className="max-w-2xl">
            <span className="overline mb-4 block animate-fade-in">
              {heroSubtitle}
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight mb-6 animate-fade-in-up">
              {heroTitle}
            </h1>
            <p className="text-lg text-slate-600 mb-8 max-w-xl animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              {t('home.hero.description', language === 'bg' ? 'Вашият надежден партньор за международни премествания, експресен транспорт и специализирани товари. Над 20 години опит.' : 'Your trusted partner for international moving, express transportation, and specialized cargo solutions. Over 20 years of experience delivering excellence.')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <Link to="/contact?quote=true">
                <button 
                  data-testid="hero-quote-btn"
                  className="btn-modern btn-primary btn-shimmer btn-lg"
                >
                  {t('home.hero.cta', language === 'bg' ? 'Безплатна оферта' : 'Get a Free Quote')}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
              <Link to="/services">
                <button 
                  data-testid="hero-services-btn"
                  className="btn-modern btn-secondary btn-lg"
                >
                  {t('home.hero.exploreServices', language === 'bg' ? 'Разгледай услугите' : 'Explore Services')}
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section data-testid="stats-section" className="bg-white py-16 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 scroll-stagger">
            {stats.map((stat, index) => (
              <div 
                key={stat.label}
                className="text-center stat-animate"
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <stat.icon className="w-8 h-8 text-[#C41E3A] mx-auto mb-3" />
                <div className="text-3xl md:text-4xl font-extrabold text-[#C41E3A] mb-1">
                  {stat.number}
                </div>
                <div className="text-slate-500 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section data-testid="services-section" className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16 scroll-animate">
            <span className="overline mb-4 block">{t('home.services.subtitle', 'WHAT WE OFFER')}</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              {t('home.services.title', 'Our Services')}
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              {language === 'bg' ? 'Цялостни транспортни решения, съобразени с вашите нужди.' : 'Comprehensive transport solutions tailored to your needs, from personal belongings to specialized cargo.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 scroll-stagger">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Truck;
              const title = language === 'bg' && service.title_bg ? service.title_bg : (service.title_en || service.title);
              const description = language === 'bg' && service.short_description_bg ? service.short_description_bg : (service.short_description_en || service.short_description);
              return (
                <Card 
                  key={service.id}
                  data-testid={`service-card-${service.id}`}
                  className="service-card bg-white border-slate-100 hover:border-[#C41E3A]/30 hover:shadow-xl hover:shadow-red-100/50 transition-all duration-300 group scroll-card rounded-3xl"
                  style={{ transitionDelay: `${index * 0.1}s` }}
                >
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mb-5 group-hover:bg-red-100 transition-colors">
                      <IconComponent className="w-7 h-7 text-[#C41E3A]" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{title}</h3>
                    <p className="text-slate-500 text-sm mb-5">{description}</p>
                    <Link 
                      to={`/services#${service.id}`}
                      className="inline-flex items-center text-[#C41E3A] text-sm font-medium hover:gap-2 transition-all"
                    >
                      {st('common.learnMore')} <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <button 
                data-testid="view-all-services-btn"
                className="btn-modern btn-secondary"
              >
                {language === 'bg' ? 'Виж всички услуги' : 'View All Services'}
                <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Image Gallery Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 scroll-stagger">
            <div className="rounded-3xl overflow-hidden aspect-[4/3] shadow-lg image-reveal" style={{ transitionDelay: '0s' }}>
              <img src={TRUCK_IMAGES.hero} alt="Tetralog truck at airport" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="rounded-3xl overflow-hidden aspect-[4/3] shadow-lg image-reveal" style={{ transitionDelay: '0.15s' }}>
              <img src={TRUCK_IMAGES.snow} alt="Tetralog truck in snow" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="rounded-3xl overflow-hidden aspect-[4/3] shadow-lg image-reveal" style={{ transitionDelay: '0.3s' }}>
              <img src={TRUCK_IMAGES.port} alt="Tetralog truck at port" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Preview */}
      <section 
        data-testid="why-choose-section"
        className="section-padding bg-slate-50"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="scroll-animate-left">
              <span className="overline mb-4 block">{language === 'bg' ? 'ЗАЩО TETRALOG' : 'Why Tetralog'}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                {whyChooseTitleMain} <span className="text-[#C41E3A]">{whyChooseTitleHighlight}</span>
              </h2>
              <p className="text-slate-500 mb-8">
                {whyChooseSubtitle}
              </p>

              <div className="space-y-4">
                {whyChooseFeatures.filter(f => f).map((feature, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-3 animate-fade-in-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <CheckCircle className="w-5 h-5 text-[#C41E3A] flex-shrink-0" />
                    <span className="text-slate-600">{feature}</span>
                  </div>
                ))}
              </div>

              <Link to="/why-choose-us" className="inline-block mt-8">
                <button 
                  data-testid="learn-more-btn"
                  className="btn-modern btn-primary btn-shimmer"
                >
                  {language === 'bg' ? 'Научете повече за нас' : 'Learn More About Us'}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
            </div>

            {/* Testimonials */}
            <div className="space-y-6 scroll-animate-right">
              {testimonials.slice(0, 3).map((testimonial, index) => (
                <Card 
                  key={testimonial.id || index}
                  className="bg-white border-slate-100 hover:shadow-lg transition-all rounded-3xl scroll-card"
                  style={{ transitionDelay: `${index * 0.15}s` }}
                >
                  <CardContent className="p-6">
                    <div className="flex gap-1 mb-3">
                      {[...Array(testimonial.rating || 5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-[#C41E3A] text-[#C41E3A]" />
                      ))}
                    </div>
                    <p className="text-slate-600 text-sm mb-4 italic">
                      &ldquo;{language === 'bg' ? (testimonial.text_bg || testimonial.text) : (testimonial.text_en || testimonial.text)}&rdquo;
                    </p>
                    <div>
                      <div className="font-bold text-slate-900">{testimonial.name}</div>
                      <div className="text-slate-400 text-sm">{testimonial.company}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-20 bg-gradient-to-r from-[#C41E3A] to-[#A01830]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center scroll-animate-scale">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('home.cta.title', language === 'bg' ? 'Готови да изпратите товар?' : 'Ready to Ship Your Cargo?')}
          </h2>
          <p className="text-white/80 mb-8">
            {t('home.cta.description', language === 'bg' ? 'Получете безплатна оферта и изпитайте разликата с Tetralog.' : 'Get a free quote today and experience the Tetralog difference.')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact?quote=true">
              <button 
                data-testid="cta-quote-btn"
                className="btn-modern btn-light btn-lg"
              >
                {language === 'bg' ? 'Заявете оферта' : 'Request a Quote'}
              </button>
            </Link>
            <Link to="/contact">
              <button 
                data-testid="cta-contact-btn"
                className="btn-modern btn-lg"
                style={{
                  background: 'transparent',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.5)',
                }}
              >
                {language === 'bg' ? 'Свържете се с нас' : 'Contact Our Team'}
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
